import type { ReactNode } from 'react';

import { cn } from '../cn';
import type { ColorName, ColorVariant, Style } from './buttonClasses';
import { colorRegistry, composeVariant, parseVariant } from './buttonClasses';
import Tooltip from './Tooltip';
import type { Size } from './types';
import { sizeHeight } from './types';

export type DateBoxButtonProps = {
  /** 3-letter month label (e.g. "JAN"). Caller formats it however they like. */
  month: string;
  /** Day-of-month label (e.g. "29"). String so callers can pass placeholders like "?". */
  day: string;
  size?: Size;
  /** Color axis — `'blue' | 'red' | …`. Defaults to `'blue'`. */
  color?: ColorName;
  /** Style axis — `'filled' | 'outline' | 'soft' | 'underline'`. Defaults to `'filled'`. */
  tone?: Style;
  faded?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  /** When false, the outer border is omitted — used when nested inside a ButtonGroup. */
  bordered?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  tooltip?: ReactNode;
  className?: string;
  /** Rendered absolutely-positioned over the box — used to overlay an invisible <input type="date">. */
  children?: ReactNode;
};

/** Subtler corner radius than the standard Button so the date pill reads as a calendar tile. */
function roundedClass(size: Size): string {
  return {
    '3xs': 'rounded-xs',
    '2xs': 'rounded-xs',
    xs: 'rounded-sm',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-md',
    xl: 'rounded-md',
    '2xl': 'rounded-lg',
    '3xl': 'rounded-lg',
    '4xl': 'rounded-xl',
  }[size];
}

/** Width matches the matching Button height (square box) so the pill stays compact at every size. */
function widthClass(size: Size): string {
  return {
    '3xs': 'w-4',
    '2xs': 'w-5',
    xs: 'w-6',
    sm: 'w-8',
    md: 'w-9',
    lg: 'w-10',
    xl: 'w-12',
    '2xl': 'w-16',
    '3xl': 'w-20',
    '4xl': 'w-24',
  }[size];
}

/** Header height — month label band. Day fills remaining space. */
function headerHeightClass(size: Size): string {
  return {
    '3xs': 'h-1.5',
    '2xs': 'h-2',
    xs: 'h-2.5',
    sm: 'h-3',
    md: 'h-3',
    lg: 'h-3.5',
    xl: 'h-4',
    '2xl': 'h-5',
    '3xl': 'h-6',
    '4xl': 'h-7',
  }[size];
}

function headerFontClass(size: Size): string {
  return {
    '3xs': 'text-[6px]',
    '2xs': 'text-[7px]',
    xs: 'text-[8px]',
    sm: 'text-[9px]',
    md: 'text-[9px]',
    lg: 'text-[10px]',
    xl: 'text-[11px]',
    '2xl': 'text-xs',
    '3xl': 'text-sm',
    '4xl': 'text-base',
  }[size];
}

function dayFontClass(size: Size): string {
  return {
    '3xs': 'text-[8px]',
    '2xs': 'text-[10px]',
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
  }[size];
}

/**
 * Solid background + contrasting text for the month header.
 * Driven from the registry's `tooltip` slot (which already encodes a
 * colored chip with high-contrast text per color, including aurora's
 * gradient and cream/white/yellow's light-bg pattern).
 */
function headerVariantClass(variant: ColorVariant): string {
  return colorRegistry[parseVariant(variant).color].tooltip;
}

/** Border color matching the header. Uses the registry's `popoverBorder` (200/700 stops). */
function borderVariantClass(variant: ColorVariant): string {
  return colorRegistry[parseVariant(variant).color].popoverBorder;
}

/**
 * Body background + day-number text color, tinted to the color (light
 * mode = color-50 bg + color-700 text, dark mode = color-950 + color-200).
 * Driven from the registry's `outline.bg` slot which already encodes both.
 */
function bodyVariantClass(variant: ColorVariant): string {
  return colorRegistry[parseVariant(variant).color].outline.bg;
}

export default function DateBoxButton({
  month,
  day,
  size = 'md',
  color,
  tone,
  faded,
  disabled,
  readOnly,
  bordered = true,
  onClick,
  onMouseEnter,
  onMouseLeave,
  tooltip,
  className,
  children,
}: DateBoxButtonProps) {
  const interactive = !readOnly && !disabled && onClick != null;
  // DateBoxButton's neutral default is blue (calendar tile), not gray.
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'blue', tone ?? 'filled');

  return (
    <Tooltip
      as={readOnly ? 'span' : 'button'}
      tooltip={tooltip}
      variant={effectiveVariant}
      {...(!readOnly && {
        disabled,
        type: 'button' as const,
        onClick,
        onMouseEnter,
        onMouseLeave,
      })}
      {...(readOnly && { onMouseEnter, onMouseLeave })}
      className={cn(
        'relative flex flex-col items-center overflow-hidden text-center box-border',
        bodyVariantClass(effectiveVariant),
        bordered && 'border',
        widthClass(size),
        sizeHeight(size),
        roundedClass(size),
        bordered && borderVariantClass(effectiveVariant),
        faded && 'opacity-30 hover:opacity-100 transition-opacity',
        disabled && 'opacity-50 cursor-not-allowed',
        interactive && 'cursor-pointer hover:shadow-xs transition-shadow',
        className,
      )}
      data-testid="date-box-button">
      {children}
      <span
        data-testid="date-box-month"
        className={cn(
          'w-full shrink-0 flex items-center justify-center font-semibold leading-none',
          headerHeightClass(size),
          headerFontClass(size),
          headerVariantClass(effectiveVariant),
        )}>
        {month}
      </span>
      <span
        data-testid="date-box-day"
        className={cn(
          'flex-1 min-h-0 w-full flex items-center justify-center font-bold leading-none tabular-nums',
          dayFontClass(size),
        )}>
        {day}
      </span>
    </Tooltip>
  );
}
